import { computed, type ComputedRef } from "vue";
import escape from "lodash/escape";
import type {
  CuiBillingFieldsLabels,
  CuiBrandingFieldsLabels,
  CuiBrandingLogosLabels,
  CuiLocaleSelectLabels,
  CuiNotificationLevelSelectLabels,
  CuiSubscriptionEditorLabels,
  CuiSubscriptionReviewLabels,
  CuiWorkspaceAdvancedSettingsLabels,
  CuiWorkspaceLimitsCardLabels,
  CuiWorkspaceValidationMessages,
} from "@coronet/cui";
import {
  getNotificationLevelLabelKey,
  getNotificationLevels,
  WorkspaceAdvancedSetting,
} from "@coronet/cui";
import { i18n } from "@/plugins/i18n";
import {
  SubscriptionAddon,
  SubscriptionBundle,
  SubscriptionModule,
  WorkspaceLocale,
  WorkspaceType,
} from "@/constants/workplaces";

/** Names of bundles, modules and add-ons, and the descriptions that exist, for the cui subscription components. */
export interface SubscriptionNames {
  bundleLabels: Record<SubscriptionBundle, string>;
  bundleDescriptions: Partial<Record<SubscriptionBundle, string>>;
  moduleLabels: Record<SubscriptionModule, string>;
  moduleDescriptions: Partial<Record<SubscriptionModule, string>>;
  addonLabels: Record<SubscriptionAddon, string>;
  addonDescriptions: Partial<Record<SubscriptionAddon, string>>;
}

/** Names on the subscription rules card, which lists each managed twin as a bundle of its own. */
export type SubscriptionRulesNames = Pick<SubscriptionNames, "bundleLabels" | "moduleLabels">;

/** What the notification-level texts of one workspace depend on. */
export interface NotificationLevelTextOptions {
  workspaceType: WorkspaceType;
  hasParent: boolean;
  /** Named by the Regular "parent" text; escaped by the builder. */
  domain: string | undefined;
}

/** Translates `key(value)` for every value of an enum. */
const translateAll = <T extends string>(
  values: readonly T[],
  key: (value: T) => string
): Record<T, string> => {
  const entries = values.map((value) => [value, i18n.global.t(key(value))]);
  return Object.fromEntries(entries) as Record<T, string>;
};

/** Whether `key` has a non-empty text in the fallback locale, which holds every key. */
const hasFallbackText = (key: string) =>
  i18n.global.te(key, WorkspaceLocale.EN_US) &&
  i18n.global.t(key, {}, { locale: WorkspaceLocale.EN_US }) !== "";

/**
 * Like `translateAll`, but skips values whose key has no text or an empty one: not every item has a
 * description (e.g. an add-on the API returns that the console has no text for), and a missing key
 * would be shown as is. The text is looked up in the fallback locale, so a description the active
 * locale lacks still falls back to it, as every other text does.
 */
const translateExisting = <T extends string>(
  values: readonly T[],
  key: (value: T) => string
): Partial<Record<T, string>> =>
  translateAll(
    values.filter((value) => hasFallbackText(key(value))),
    key
  );

const BUNDLES = Object.values(SubscriptionBundle);
const MODULES = Object.values(SubscriptionModule);
const ADDONS = Object.values(SubscriptionAddon);

export interface WorkspaceFormText {
  validationMessages: ComputedRef<CuiWorkspaceValidationMessages>;
  billingLabels: ComputedRef<CuiBillingFieldsLabels>;
  limitsLabels: ComputedRef<CuiWorkspaceLimitsCardLabels>;
  brandingLabels: ComputedRef<CuiBrandingFieldsLabels>;
  logosLabels: ComputedRef<CuiBrandingLogosLabels>;
  localeSelectLabels: ComputedRef<CuiLocaleSelectLabels>;
  localeDisplayNames: ComputedRef<Record<WorkspaceLocale, string>>;
  subscriptionEditorLabels: ComputedRef<CuiSubscriptionEditorLabels>;
  subscriptionEditorNames: ComputedRef<SubscriptionNames>;
  subscriptionReviewLabels: ComputedRef<CuiSubscriptionReviewLabels>;
  subscriptionReviewNames: ComputedRef<SubscriptionNames>;
  subscriptionRulesNames: ComputedRef<SubscriptionRulesNames>;
  advancedSettingsLabels: ComputedRef<CuiWorkspaceAdvancedSettingsLabels>;
  /** Hint on an add-on card whose module is not selected; HTML (the module name is bold). */
  addonDisabledHint: (moduleLabel: string) => string;
  /** Title and the HTML text of each level `CuiNotificationLevelSelect` shows for this workspace. */
  notificationLevelLabels: (
    options: NotificationLevelTextOptions
  ) => CuiNotificationLevelSelectLabels;
}

/**
 * Text for the shared @coronet/cui workspace form components (cui takes no i18n), built from the
 * console's existing translation keys. Everything is computed, so a runtime locale switch updates
 * labels and validation messages alike.
 */
export function useWorkspaceFormText(): WorkspaceFormText {
  const validationMessages = computed<CuiWorkspaceValidationMessages>(() => ({
    required: i18n.global.t("validations.required"),
    email: i18n.global.t("validations.email"),
    domain: i18n.global.t("validations.domain"),
    domainZoneIsNotAllowed: i18n.global.t("validations.domainZoneIsNotAllowed"),
    invalidHexColor: i18n.global.t("validations.invalidHexColor"),
    invalidSubdomain: i18n.global.t("validations.invalidSubdomain"),
    noSpacesAllowed: i18n.global.t("validations.noSpacesAllowed"),
    maxNCharacters: (n) => i18n.global.t("validations.maxNCharacters", { n }),
  }));

  const billingLabels = computed<CuiBillingFieldsLabels>(() => ({
    title: i18n.global.t("modals.createWorkspace.billing"),
    netSuiteId: i18n.global.t("modals.editWorkspace.netSuiteId"),
    netSuiteLegacySubscriptionId: i18n.global.t(
      "modals.editWorkspace.netSuiteLegacySubscriptionId"
    ),
    newNetSuiteSubscriptionId: i18n.global.t("modals.editWorkspace.newNetSuiteSubscriptionId"),
    netSuiteSubscriptionId: i18n.global.t("modals.editWorkspace.netSuiteSubscriptionId"),
    nfrWorkspace: i18n.global.t("modals.editWorkspace.nfrWorkspace"),
    acvAccount: i18n.global.t("modals.editWorkspace.acvAccount"),
    rampProgram: i18n.global.t("modals.editWorkspace.rampProgram"),
    minimumCommitment: i18n.global.t("modals.editWorkspace.minimumCommitment"),
    onlyDigitsValidation: i18n.global.t("validations.onlyDigits"),
    rampRequiresNetSuiteIdsValidation: i18n.global.t("validations.rampRequiresNetSuiteIds"),
    requiredValidation: i18n.global.t("validations.required"),
    onlyIntegerValidation: i18n.global.t("validations.onlyInteger"),
  }));

  const limitsLabels = computed<CuiWorkspaceLimitsCardLabels>(() => ({
    unlimited: i18n.global.t("modals.editWorkspace.userDeviceLimits.unlimited"),
    limited: i18n.global.t("modals.editWorkspace.userDeviceLimits.limited"),
    limitedHelperText: i18n.global.t("modals.editWorkspace.userDeviceLimits.limitedHelperText"),
    numberOfProtectedUsers: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedUsers"
    ),
    numberOfProtectedDevices: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedDevices"
    ),
    numberOfProtectedDevicesHint: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.numberOfProtectedDevicesHint"
    ),
    overCapacityAllowance: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.overCapacityAllowance"
    ),
    overCapacityAllowanceHint: i18n.global.t(
      "modals.editWorkspace.userDeviceLimits.overCapacityAllowanceHint"
    ),
    summaryTitle: i18n.global.t("modals.editWorkspace.userDeviceLimits.summaryTitle"),
    totalMaxUsers: (total) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.totalMaxUsers", { n: total }),
    totalMaxDevices: (total) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.totalMaxDevices", { n: total }),
    // Spread: vue-i18n wants an index-signature object, and the cui params are interfaces.
    protectedUsersValidationWithCap: (params) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithCap", {
        ...params,
      }),
    protectedUsersValidationWithoutCap: (params) =>
      i18n.global.t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithoutCap", {
        ...params,
      }),
    requiredValidation: i18n.global.t("validations.required"),
    onlyIntegerValidation: i18n.global.t("validations.onlyInteger"),
  }));

  const brandingLabels = computed<CuiBrandingFieldsLabels>(() => ({
    brandColorLabel: i18n.global.t("modals.editWorkspace.branding.brandColorLabel"),
    brandColorHint: i18n.global.t("modals.editWorkspace.branding.brandColorHint"),
    aliasLabel: i18n.global.t("modals.editWorkspace.branding.aliasLabel"),
    aliasHint: i18n.global.t("modals.editWorkspace.branding.aliasHint"),
    aliasFullDomainHint: (alias) =>
      i18n.global.t("modals.editWorkspace.branding.aliasFullDomainHint", { domain: alias }),
    noReplyEmailLabel: i18n.global.t("modals.editWorkspace.branding.noReplyEmailLabel"),
    noReplyEmailHint: i18n.global.t("modals.editWorkspace.branding.noReplyEmailHint"),
    supportEmailLabel: i18n.global.t("modals.editWorkspace.branding.supportEmailLabel"),
    supportEmailHint: i18n.global.t("modals.editWorkspace.branding.supportEmailHint"),
    companyAddressLabel: i18n.global.t("modals.editWorkspace.branding.companyAddressLabel"),
    companyAddressHint: i18n.global.t("modals.editWorkspace.branding.companyAddressHint"),
  }));

  const logosLabels = computed<CuiBrandingLogosLabels>(() => ({
    imageHint: i18n.global.t("logoUploader.imageHint"),
    imageDescription: i18n.global.t("logoUploader.description"),
    emailImageHint: i18n.global.t("logoUploader.emailImageHint"),
    emailImageDescription: i18n.global.t("logoUploader.emailDescription"),
    replaceImage: i18n.global.t("logoUploader.replaceImage"),
    imageError: i18n.global.t("logoUploader.imageError"),
  }));

  const localeSelectLabels = computed<CuiLocaleSelectLabels>(() => ({
    label: i18n.global.t("modals.createWorkspace.language.label"),
    placeholder: i18n.global.t("modals.createWorkspace.language.placeholder"),
    requiredValidation: i18n.global.t("validations.required"),
  }));

  const localeDisplayNames = computed(() =>
    translateAll(Object.values(WorkspaceLocale), (locale) => `general.localeDisplayNames.${locale}`)
  );

  // The keys the console's old subscription card used, so the editor reads the same.
  const subscriptionEditorLabels = computed<CuiSubscriptionEditorLabels>(() => ({
    requiredError: i18n.global.t("validations.bundleOrModuleIsRequired"),
    singleBundleInfo: i18n.global.t("modals.createWorkspace.onlyOneBundleCanBeSelected"),
    bundlesTab: i18n.global.t("subscriptions.bundles.title"),
    legacyBundlesTab: i18n.global.t("subscriptions.legacyBundles"),
    modulesTab: i18n.global.t("subscriptions.modules.title"),
    recommendedModules: i18n.global.t("modals.createWorkspace.recommendedModules"),
    addons: i18n.global.t("modals.createWorkspace.addons"),
    includedInBundles: i18n.global.t("modals.createWorkspace.includedInBundles"),
    includedInBundle: i18n.global.t("modals.createWorkspace.includedInBundle"),
    select: i18n.global.t("general.select"),
    selected: i18n.global.t("general.selected"),
    addon: i18n.global.t("general.addon"),
    bundleUnmanaged: i18n.global.t("rangeBundleSelector.unmanaged"),
    bundleManaged: i18n.global.t("rangeBundleSelector.managed"),
    bundleManagedTooltip: i18n.global.t("rangeBundleSelector.managedTooltip"),
    moduleUnmanaged: i18n.global.t("moduleOrAddonSelector.unmanaged"),
    moduleManaged: i18n.global.t("moduleOrAddonSelector.managed"),
    moduleManagedTooltip: i18n.global.t("moduleOrAddonSelector.managedTooltip"),
    soc: i18n.global.t("subscriptions.addons.soc"),
    socDescription: i18n.global.t("moduleOrAddonSelector.soc.description"),
  }));

  // The editor and the review name bundles and add-ons the same way.
  const bundleLabels = computed(() =>
    translateAll(BUNDLES, (bundle) => `manageWorkspaces.bundles.${bundle}`)
  );
  const addonLabels = computed(() =>
    translateAll(ADDONS, (addon) => `subscriptions.addons.${addon}`)
  );

  const subscriptionEditorNames = computed<SubscriptionNames>(() => ({
    bundleLabels: bundleLabels.value,
    bundleDescriptions: translateExisting(
      BUNDLES,
      (bundle) => `rangeBundleSelector.${bundle}.description`
    ),
    moduleLabels: translateAll(MODULES, (module) => `subscriptions.modules.${module}`),
    moduleDescriptions: translateExisting(
      MODULES,
      (module) => `moduleOrAddonSelector.${module}.description`
    ),
    addonLabels: addonLabels.value,
    addonDescriptions: translateExisting(
      ADDONS,
      (addon) => `moduleOrAddonSelector.${addon}.description`
    ),
  }));

  const subscriptionReviewLabels = computed<CuiSubscriptionReviewLabels>(() => ({
    summaryTitle: i18n.global.t("modals.createWorkspace.summary.title"),
    bundles: i18n.global.t("modals.createWorkspace.summary.bundles"),
    modulesAndAddons: i18n.global.t("modals.createWorkspace.summary.modulesAndAddons"),
  }));

  // The create review's own module names and bundle subtitles; module rows inside a bundle and
  // module subtitles come from the modal's slots, and standalone add-ons had no subtitle.
  const subscriptionReviewNames = computed<SubscriptionNames>(() => ({
    bundleLabels: bundleLabels.value,
    bundleDescriptions: translateExisting(
      BUNDLES,
      (bundle) => `modals.createWorkspace.summary.${bundle}.subtitle`
    ),
    moduleLabels: translateAll(
      MODULES,
      (module) => `modals.createWorkspace.summary.${module}.title`
    ),
    moduleDescriptions: {},
    addonLabels: addonLabels.value,
    addonDescriptions: {},
  }));

  // The rules card's own keys: it lists each managed twin as a "Managed …" bundle, while the
  // editor shows one card per pair.
  const subscriptionRulesNames = computed<SubscriptionRulesNames>(() => ({
    bundleLabels: translateAll(BUNDLES, (bundle) => `subscriptions.bundles.${bundle}`),
    moduleLabels: translateAll(MODULES, (module) => `subscriptions.modules.${module}`),
  }));

  const advancedSettingsLabels = computed<CuiWorkspaceAdvancedSettingsLabels>(() => ({
    title: i18n.global.t("modals.createWorkspace.advancedSettings"),
    [WorkspaceAdvancedSetting.SUPPORT_ENABLED]: i18n.global.t(
      "modals.createWorkspace.showSupportLink"
    ),
    [WorkspaceAdvancedSetting.SCAN_UNPROTECTED_USERS]: i18n.global.t(
      "modals.createWorkspace.scanUnprotectedUsers"
    ),
    [WorkspaceAdvancedSetting.SHOW_DISABLED_MODULES]: i18n.global.t(
      "modals.createWorkspace.showDisabledModules"
    ),
    [WorkspaceAdvancedSetting.CORO_EMPLOYEE_WORKSPACE]: i18n.global.t(
      "modals.createWorkspace.coroEmployeeWorkspace"
    ),
  }));

  const addonDisabledHint = (moduleLabel: string) =>
    i18n.global.t("moduleOrAddonSelector.addonDisabled", { module: moduleLabel });

  const notificationLevelLabels = ({
    workspaceType,
    hasParent,
    domain,
  }: NotificationLevelTextOptions): CuiNotificationLevelSelectLabels => {
    // The select renders these texts as HTML, and the domain is workspace data the backend does
    // not format-check, so it is escaped before it goes into them.
    const named = { domain: escape(domain ?? "") };
    const keys = getNotificationLevels(workspaceType, { hasParent }).map((level) =>
      getNotificationLevelLabelKey(workspaceType, level, { hasParent })
    );
    return {
      title: i18n.global.t("modals.createWorkspace.notifications"),
      levels: Object.fromEntries(
        keys.map((key) => [
          key,
          i18n.global.t(`modals.createWorkspace.notificationLevels.${workspaceType}.${key}`, named),
        ])
      ),
    };
  };

  return {
    validationMessages,
    billingLabels,
    limitsLabels,
    brandingLabels,
    logosLabels,
    localeSelectLabels,
    localeDisplayNames,
    subscriptionEditorLabels,
    subscriptionEditorNames,
    subscriptionReviewLabels,
    subscriptionReviewNames,
    subscriptionRulesNames,
    advancedSettingsLabels,
    addonDisabledHint,
    notificationLevelLabels,
  };
}
